import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDataListActions } from './useDataListActions';
import { resetFilters, sidebarSelector } from '../redux/slicers/admin/sidebarSlicer';
import { filterSelector, selectParamsInSequence, selectParamsInSequenceFormatted, totalMembersInOrg } from '../redux/slicers/filters/filterSlicer';
import { FILTER } from '../components/admin/curator/CuratorFilter/CuratorFilter';
import { useUserContext } from '../contexts/UserContext';
import { debounce, isEmpty } from 'lodash';
import { selectSearchValue } from '../redux/slicers/search/searchSlicer';
import { addToCollectionActions } from '../redux/slicers/addToCollectionPopupSlicer';
import { isCuratorPage } from '../helpers/urlHelper';
import { LogCurator } from '../helpers/logEvents';
import { SHARED_FILES_ROUTE } from "../constants/routePathConstants";

export const useDataList = ({
  selector,
  resetAction,
  getAction,
  itemDeleteText,
  itemsDeleteText,
  requestParams,
  disableFilters,
  filterByKey,
  defaultSort,
  useGlobalFilter,
  useGlobalSearch,
  defaultBrands,
  defaultCreatedBy,
  defaultFilterBy,
  skipBrandFilter,
  hookName,
  actions,
  customSearchStr,
  searchDelay = 400,
  pendingIdsStr, // this will not trigger refetch -> this parameter will be included only with next request -> keep it this way -> it's required for correct work of upload textures in curator
  createdByKey = 'created_by',
  isManage,
  isReplacePropActive,
  extraParams = {}
}) => {
  const dispatch = useDispatch();
  const fetchedOnceRef = useRef(false);
  const fetchedOnce = fetchedOnceRef.current;
  const [mounted, setMounted] = useState(false);


  const {
    data,
    perPage,
    page,
    list,
    loading,
    hasData,
    filterValue,
    dataLoadedOnce,
    filter,
    refetchCounter,
    triggerNextPageCounter,
  } = useSelector(selector);
  const globalSearch = useSelector(selectSearchValue);
  const selectedMembers = useSelector((state) => filterSelector(state).members);
  const { filterValues } = useSelector(sidebarSelector);

  const {
    selected,
    sortBy,
    filterBy,
    search,
    tags,
    hasSelected,
    deleteDisabled,
    allSelected,
    anySelected,

    onDelete,
    toggleAll,
    resetSelection,
    toggleItem,
    toggleSingleItem,
    onSortChange,
    onFilterByChange,
    onSearchChange,
  } = useDataListActions({
    items: list,
    itemDeleteText,
    itemsDeleteText,
    defaultSort,
    defaultFilterBy,
    useGlobalFilter,
  });
  const hasMore = data.current_page_number < data.total_pages;
  const { currentUser } = useUserContext();
  const organisationId = currentUser?.organization;

  const _filterValue = filterValue && !useGlobalFilter ? filterValue : filterValues;

  const hasFilters = Boolean(filter?.filteroptions?.length);
  const searchActive = search && !_filterValue.length && hasFilters;
  const totalOrgMembers = useSelector(totalMembersInOrg);
  const paramsInSequence = useSelector(selectParamsInSequence);

  const fetchData = async (page) => {
    if (!mounted || (isManage && !Object.keys(requestParams).length && !requestParams.id && isReplacePropActive)) return;
    fetchedOnceRef.current = true;

    const filterParams = filterToParams(_filterValue, {
      organisationId,
      userId: currentUser?.id,
      defaultBrands,
    });

    // created_by
    let created_by = defaultCreatedBy;
    if (selectedMembers.length) {
      created_by = totalOrgMembers === selectedMembers.length ? '' : selectedMembers.map((m) => m.id);
    } else if (requestParams?.created_by) {
      created_by = requestParams?.created_by;
    }

    // filter by
    let filterByValue;
    if (filterBy?.key) {
      filterByValue = {
        [filterBy.key]: typeof filterBy.apiValue === 'boolean' ? filterBy.apiValue : filterBy.value,
      };
    } else if (filterByKey) {
      filterByValue = { [filterByKey]: filterBy && typeof filterBy === 'object' ? filterBy.value : filterBy };
    }

    const requestParamsCopy = { ...requestParams };
    delete requestParamsCopy.created_by;
    let searchStr = customSearchStr 
      ? customSearchStr 
      : useGlobalSearch ? globalSearch : search;


    const actionParams = {
      ...(disableFilters ? {} : filterParams),
      page,
      per_page: perPage,
      search: searchStr,
      tags: tags,
      sort: typeof sortBy === 'object' ? sortBy.value : sortBy,
      skipBrandFilter,
      ...filterByValue,
      ...requestParamsCopy,
      ...(created_by ? { [createdByKey]: created_by } : {}),
    }

    if (pendingIdsStr) {
      actionParams.include_pending_ids = pendingIdsStr;
    }

    // LogCurator("actionParams: ", actionParams)

    let updatedActionParams = {
      ...requestParamsCopy,
      ...(disableFilters ? {} : filterParams),
      ...(created_by ? { [createdByKey]: created_by } : {})
    }
    paramsInSequence.map((a,i) => {
      const key = Object.keys(a)[0];
      if(key === 'filterBy') {
        const k = Object.keys(a[key])
        updatedActionParams[k] = a[key][k]
      } else if(key === 'shared') {
        Object.keys(a[key]).map(b => {
          updatedActionParams[b] = a[key][b]
        })
      } else {
        if(key === 'created_by' && createdByKey === 'added_by') {
          delete updatedActionParams[createdByKey]
          updatedActionParams[createdByKey] = a[key]
        } else {
          delete updatedActionParams[key]
          updatedActionParams[key] = a[key]
        }
      }
    })

    updatedActionParams = {
      ...updatedActionParams,
      page,
      per_page: perPage,
      search: searchStr,
      sort: typeof sortBy === 'object' ? sortBy.value : sortBy,
      skipBrandFilter,
    }

    if(updatedActionParams['is_shared']) {
      const {brands, ...restParams} = updatedActionParams;

      updatedActionParams = {
        ...restParams
      }
    }

    if(defaultFilterBy && defaultFilterBy?.key && defaultFilterBy?.value) {
      updatedActionParams[defaultFilterBy?.key] = defaultFilterBy?.apiValue
    }

    // LogCurator("updatedActionParams: ", updatedActionParams)
    
    if (window.location.pathname === SHARED_FILES_ROUTE) {
      created_by = null
      delete requestParams?.created_by
      delete updatedActionParams?.created_by
    }

    if (!isEmpty(extraParams)) {
      updatedActionParams.extra_params = extraParams;
    }

    if ("skip_sort" in updatedActionParams) {
      const skipSort = Boolean(updatedActionParams.skip_sort);
      if (skipSort) updatedActionParams.sort = null;
      delete updatedActionParams.skip_sort;
    }

    // await dispatch(getAction(isCuratorPage() ? actionParams : updatedActionParams));
    await dispatch(getAction(updatedActionParams));
  };


  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData


  // useEffect(() => {
  //   if (!fetchedOnce) return;
  //   dispatch(resetFilters());
  // }, [filterBy]);

  useEffect(() => {
    if (refetchCounter > 0) {
      // at each counter change we refresh the whole list
      dispatch(resetAction());
      fetchData(1);
    }
  }, [refetchCounter]);

  useEffect(() => {
    if (triggerNextPageCounter > 0) {
      // at each counter change we fetch next page
      fetchMoreData();
    }
  }, [triggerNextPageCounter]);

  useEffect(() => {
    if (!fetchedOnce) return;

    if (useGlobalFilter) {
      dispatch(resetFilters());
    }
  }, [organisationId]);


  let FETCH_DATA_ALREADY_TRIGGERED = false; // keep state if fetch data was triggered during this render once -> so we will not trigger two requests at the same time. This was happening when we were resetting filterBy(and other filters) and search at the same time which was triggerring fetch data requests from 2 separate hooks

  useEffect(() => {
    if (window.$restrictFilter) {
      fetchedOnceRef.current = true;
    }
    if(window.$restrictFilter) return
    dispatch(resetAction());
    fetchData(1);
    FETCH_DATA_ALREADY_TRIGGERED = true;
  }, [
    _filterValue,
    sortBy,
    filterBy,
    selectedMembers,
    requestParams,
    getAction,
    organisationId,
    mounted,
  ]);

  const fetchFirstPage = () => {
    dispatch(resetAction());
    fetchData(1);
  };

  const fetchFirstPageDelayed = useCallback(debounce(() => {
    dispatch(resetAction());
    fetchDataRef.current(1);
  }, searchDelay), [])



  useEffect(() => {
    if(window.$restrictFilter) return
    if (!fetchedOnce) return;
    if (FETCH_DATA_ALREADY_TRIGGERED) return;
    if (actions?.setLoading) {
      dispatch(actions.setLoading(true));
    }

    // dispatch(resetAction());
    fetchFirstPageDelayed();
    // fetchFirstPage();
  }, [search, tags, useGlobalSearch && globalSearch, customSearchStr])

  useEffect(() => {
    setMounted(true);
  }, []);

  const fetchMoreData = () => {
    window.$restrictFilter = null
    return fetchData(page + 1);
  };

  return {
    data,
    list,
    loading: loading || !fetchedOnce,
    fetchMoreData,
    hasMore,
    total: data.count,
    hasData,
    anySelected,
    filterBy,
    sortBy,
    dataLoadedOnce,
    search,

    selected,
    onDelete,
    toggleAll,
    toggleItem,
    toggleSingleItem,
    hasSelected,
    deleteDisabled,
    allSelected,
    onSortChange,
    onFilterByChange,
    onSearchChange,
    fetchFirstPage,
    refetchList: fetchFirstPage,
    resetSelection,
  };
};

export const useSimpleDataList = ({
  selector,
  resetAction,
  getAction,
  itemDeleteText,
  itemsDeleteText,
  requestParams,
  filterSelector,
  skip,
  defaultSort,
  actions,
}) => {
  const dispatch = useDispatch();

  const { data, perPage, page, list, prevList, loading, hasData, dataLoadedOnce, refetchCounter } =
    useSelector(selector);
  const { currentUser } = useUserContext();
  const organisationId = currentUser?.organization;
  const {
    selected,
    sortBy,
    filterBy,
    search,
    tags,
    hasSelected,
    deleteDisabled,
    allSelected,

    onDelete,
    toggleAll,
    toggleItem,
    onSortChange,
    onSearchChange,
    onFilterByChange,
  } = useDataListActions({
    items: list,
    itemDeleteText,
    itemsDeleteText,
    defaultSort,
  });
  const hasMore = data.current_page_number < data.total_pages;

  const fetchData = async (page) => {
    return await dispatch(
      getAction({ page, perPage, search, tags, sort: sortBy, project_type: filterBy, ...requestParams })
    );
  };

  useEffect(() => {
    if (skip) return;

    dispatch(resetAction());
    fetchData(1);
  }, [sortBy, filterBy, search, tags, requestParams, skip, organisationId]);

  const fetchMoreData = () => {
    return fetchData(page + 1);
  };

  useEffect(() => {
    if (refetchCounter > 0) {
      // at each counter change we refresh the whole list
      dispatch(resetAction());
      fetchData(1);
    }
  }, [refetchCounter]);

  async function loadAllPages(currentPage = page) {
    if (currentPage >= data.total_pages) return;
    const nextPage = currentPage + 1;
    await fetchData(nextPage);
    loadAllPages(nextPage);
  }

  const onPageChange = (data) => {
    const page = typeof data === 'number' ? data : data.selected + 1;

    fetchData(page);
  };

  const onPerPageChange = (perPage) => {
    dispatch(actions.setPerPage(perPage));
  };

  const fetchFirstPage = () => {
    dispatch(resetAction());
    fetchData(1);
  };

  return {
    data,
    list,
    prevList,
    loading,
    fetchMoreData,
    hasMore,
    search,
    tags,
    total: data.count,
    totalPages: data.total_pages,
    hasData,
    dataLoadedOnce,
    page,
    perPage,

    selected,
    onDelete,
    toggleAll,
    toggleItem,
    hasSelected,
    deleteDisabled,
    allSelected,
    onSortChange,
    onFilterByChange,
    onSearchChange,
    onPageChange,
    fetchData,
    loadAllPages,
    onPerPageChange,
    refetchList: fetchFirstPage
  };
};

export const getFilterOrder = (values) => {
  const findValue = (type) => {
    const index = values.findIndex((v) => v?.startsWith(type));
    return index === -1 ? Infinity : index;
  };

  const baseOrder = [
    { name: 'collections', order: findValue('collection') },
    { name: 'brands', order: findValue('brand') },
    { name: 'props', order: findValue('prop') },
    { name: 'options', order: findValue('option') },
  ];

  const order = baseOrder.sort((a, b) => a.order - b.order).map((a) => a.name);

  return { order, baseOrder };
};

export const filterToParams = (filterValues, { organisationId, userId, defaultBrands } = {}) => {
  let filterParams = {};
  const options = {};
  let collections = '';
  let brands = '';
  let props = '';

  const texturesSelected = filterValues.includes(FILTER.TEXTURES);
  const uploadsSelected = filterValues.includes(FILTER.UPLOADS);

  filterValues.forEach((value) => {
    if (value === 'favourite') {
      filterParams.is_favourite = true;
      return;
    }

    if (value === 'archive') {
      filterParams.is_archive = true;
      return;
    }

    if (value?.startsWith('option_')) {
      const [, optionId, labelId] = value.split('_');
      const optionKey = `fl_${optionId}`;

      if (!options[optionKey]) {
        options[optionKey] = labelId;
        return;
      }

      options[optionKey] += `,${labelId}`;
      return;
    }

    if (value?.startsWith('brand_')) {
      const [, brandId] = value.split('_');

      if (!brands) {
        brands = brandId;
        return;
      }

      brands += `,${brandId}`;
      return;
    }

    if (value?.startsWith('collection_')) {
      const [, collectionId] = value.split('_');

      if (!collections) {
        collections = collectionId;
        return;
      }

      collections += `,${collectionId}`;
      return;
    }

    if (value?.startsWith('prop_')) {
      const [, propId] = value.split('_');

      if (!props) {
        props = propId;
        return;
      }

      props += `,${propId}`;
      return;
    }

    if (value === FILTER.MY_3D_MODELS) {
      filterParams.brands = filterParams.brands
        ? `${filterParams.brands},${organisationId}`
        : organisationId;
    }

    if (value === FILTER.MY_TEXTURES) {
      filterParams.is_map = false;
      filterParams.created_by = userId;
      return;
    }

    if (value === FILTER.ALL_TEXTURES) {
      filterParams.is_map = false;
      return;
    }

    if (value === FILTER.MY_UPLOADS) {
      filterParams.created_by = userId;
      filterParams.is_map = true;
      return;
    }

    if (value === FILTER.ALL_UPLOADS) {
      filterParams.is_map = true;
      return;
    }

    if (value === FILTER.ALL_USER_PROPS) {
      filterParams.assign_to_all_org = true;
      return;
    }

    if (value === FILTER.MATTRESS_BUILDER) {
      filterParams.is_mattress_material = true;
      return;
    }

    if (value === FILTER.LIVE_FURNISH) {
      filterParams.live_furnish = true;
      return;
    }

    if (value === FILTER.GROUP_PROP) {
      filterParams.is_prop_group = true;
      return;
    }

    if (value === FILTER.USER_MODEL_UPLOADED) {
      filterParams.is_user_model_uploaded = true;
      return;
    }

    const isTextures = value === FILTER.TEXTURES;
    const isUploads = value === FILTER.UPLOADS;

    if ((isTextures || isUploads) && texturesSelected && uploadsSelected) {
      // we don't need to filter by is_map=true|false as both variants are selected
      return;
    }

    if (isTextures) {
      filterParams.is_map = false;
      return;
    }

    if (isUploads) {
      filterParams.is_map = true;
      return;
    }

    if (value === FILTER.OWNED_BY_ME) {
      filterParams.created_by = userId;
      return;
    }
  });

  // we want to keep selection order of filters in api query :(
  const { order } = getFilterOrder(filterValues);
  const tempParams = { collections, options, brands, props };
  order.reverse().forEach((name) => {
    if (!tempParams[name]) return;

    if (name === 'options') {
      filterParams = {
        ...tempParams[name],
        ...filterParams,
      };
    } else {
      filterParams = {
        [name]: tempParams[name],
        ...filterParams,
      };
    }
  });

  if (!filterParams.brands && defaultBrands) {
    filterParams = {
      brands: defaultBrands,
      ...filterParams,
    };
  }

  return filterParams;
};

export const useThumbnailCardDataList = (config) => {
  const dispatch = useDispatch();
  const data = useDataList({ ...config, searchDelay: 0 });

  const selectedList = data.list.filter((item) => data.selected.includes(item.id));
  const list = data.list.map((item) => ({ ...item, selected: data.selected.includes(item.id) }));

  const addToCollection = () => {
    dispatch(
      addToCollectionActions.add({
        items: selectedList,
        modelType: config.modelType,
      })
    );
  };

  return {
    ...data,
    addToCollection,
    list,
    selectedList,
  };
};
